import React from 'react'
import styled from 'styled-components'
import { StoredTransaction, isOrder } from '../../providers/transactions/model'
import { Colors } from '../../constants'
import { Check, Close } from '../icons'

const CONFIRMATION_TIME = 5 * 60 * 1000
const DELAY = 5 * 60 * 1000

interface Props {
  className?: string
  tx: StoredTransaction
  now: Date
}

export function ProgressBar({ className, tx, now }: Props) {
  let steps
  let progress = 0
  if (isOrder(tx)) {
    let isEnqueued = false
    let isDelayed = false
    let isExecuted = false
    let isReverted = false
    let isFailed = false
    let delayTime = '5 mins'
    progress = 33 * Math.min(1.0, (now.getTime() - tx.submittedAt) / CONFIRMATION_TIME)

    if (tx.receipt) {
      if (tx.orderStatus) {
        if (tx.orderStatus.status === 'enqueued') {
          isEnqueued = true

          const timestamp = new Date(tx.orderStatus.validAfterTimestamp)
          delayTime = `${timestamp.getHours().toString().padStart(2, '0')}:${timestamp
            .getMinutes()
            .toString()
            .padStart(2, '0')}:${timestamp.getSeconds().toString().padStart(2, '0')} ${
            timestamp.getHours() < 12 ? 'AM' : 'PM'
          }`
          if (now >= timestamp) {
            isDelayed = true
            progress = 66 + 23 * Math.min(1.0, (now.getTime() - timestamp.getTime()) / CONFIRMATION_TIME)
          } else {
            progress = 33 + (33 * (DELAY + now.getTime() - timestamp.getTime())) / DELAY
          }
        } else if (tx.orderStatus.status === 'executed') {
          isEnqueued = true
          isDelayed = true
          isExecuted = true
          progress = 100
        } else if (tx.orderStatus.status === 'reverted') {
          isReverted = true
          progress = 100
        } else if (tx.orderStatus.status === 'failed') {
          isFailed = true
          progress = 33
        }
      }
    }

    steps = (
      <>
        <Step complete={true} reverted={isReverted}>
          <i>
            <CheckStyled />
          </i>
          <small>submitted</small>
        </Step>
        <Step complete={isEnqueued} reverted={isReverted || isFailed}>
          <i>{isFailed ? <CloseStyled /> : <CheckStyled />}</i>
          <small>{isEnqueued ? 'enqueued' : 'enqueueing'}</small>
        </Step>
        <Step complete={isDelayed} reverted={isReverted}>
          <i>
            <CheckStyled />
          </i>
          <small>{isDelayed ? 'delay elapsed' : delayTime}</small>
        </Step>
        <Step complete={isExecuted} reverted={isReverted}>
          <i>{isReverted ? <CloseStyled /> : <CheckStyled />}</i>
          <small>{isReverted ? 'reverted' : isExecuted ? 'executed' : 'executing'}</small>
        </Step>
      </>
    )
  } else {
    let isExecuted = false
    let isDroppedAndReplaced = false
    progress = 90 * Math.min(1.0, (now.getTime() - tx.submittedAt) / CONFIRMATION_TIME)

    if (tx.droppedAndReplaced) {
      isDroppedAndReplaced = true
      progress = 100
    } else if (tx.receipt) {
      isExecuted = true
      progress = 100
    }

    steps = (
      <>
        <Step complete={true}>
          <i>
            <CheckStyled />
          </i>
          <small>submitted</small>
        </Step>
        <Step complete={isExecuted} reverted={isDroppedAndReplaced}>
          <i>{isDroppedAndReplaced ? <CloseStyled /> : <CheckStyled />}</i>
          <small>{isDroppedAndReplaced ? 'dropped & replaced' : isExecuted ? 'executed' : 'executing'}</small>
        </Step>
      </>
    )
  }

  return (
    <Container className={className} progress={progress}>
      {steps}
    </Container>
  )
}

interface ContainerProps {
  progress: number
}

export const Container = styled.ol.attrs((props: ContainerProps) => ({
  progress: props.progress,
}))`
  margin-top: 5px;
  padding: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  list-style: none;

  &:before {
    position: absolute;
    top: 8px;
    left: 2px;
    display: block;
    content: '';
    width: calc(100% - 4px);
    height: 2px;
    background-color: ${Colors.LightGray};
  }

  &:after {
    position: absolute;
    top: 8px;
    left: 2px;
    display: block;
    content: '';
    width: calc(${(props) => props.progress}% - 4px);
    height: 2px;
    background-color: ${Colors.Hover};
  }
`

interface StepProps {
  complete: boolean
  reverted?: boolean
}

export const Step = styled.li.attrs((props: StepProps) => ({
  complete: props.complete,
  reverted: props.reverted,
}))`
  position: relative;
  display: flex;
  justify-content: center;
  height: 32px;
  z-index: 1;

  i {
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: 2px solid ${(props) => (props.reverted ? Colors.Red : props.complete ? Colors.Hover : Colors.LightGray)};
    border-radius: 9px;
    background-color: ${(props) => (props.reverted ? Colors.Red : props.complete ? Colors.Hover : Colors.White)};

    svg {
      display: ${(props) => (props.reverted || props.complete ? 'initial' : 'none')};
    }
  }

  small {
    position: absolute;
    top: 21px;
    display: block;
    font-size: 10px;
    text-transform: uppercase;
    color: ${Colors.LighterGray};
    white-space: nowrap;
  }

  &:first-child {
    small {
      left: 0;
    }
  }

  &:last-child {
    small {
      right: 0;
    }
  }
`

const CheckStyled = styled(Check)`
  width: 12px;
  height: 12px;
  color: ${Colors.White};
`

const CloseStyled = styled(Close)`
  width: 12px;
  height: 12px;
  color: ${Colors.White};
`
